import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useSelector } from "react-redux";
import {
  previewAvailabilityRequest,
  sendAvailabilityRequest,
} from "../../api/availability";

// Fecha local en AAAA-MM-DD, que es lo que usa <input type="date">. No
// toISOString(): pasa a UTC y de noche en Argentina daría el día siguiente.
const toInputDate = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const inDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toInputDate(date);
};

/**
 * Vista previa y envío del pedido de disponibilidad (mail 7).
 *
 * Sale un mail por persona a cada director o evaluador que todavía no cargó.
 * La vista previa muestra el del primero como ejemplo: entre uno y otro solo
 * cambian el nombre y el rol. Como el aviso de entrega final, deja elegir el
 * Reply-To y la fecha límite, que va en ${fecha_limite}.
 */
const AvailabilityRequestDialog = ({ open, period, onClose, onSent, onEditTemplate }) => {
  const user = useSelector((state) => state.user);
  const [preview, setPreview] = useState(null);
  const [replyTo, setReplyTo] = useState("");
  const [deadline, setDeadline] = useState("");
  const replyToLoaded = useRef(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setError(null);
    setResult(null);
    setReplyTo("");
    replyToLoaded.current = false;
    setDeadline(inDays(7));
  }, [open]);

  useEffect(() => {
    if (!open || !period?.id || !deadline) return;
    let cancelled = false;
    setError(null);
    setLoading(true);
    previewAvailabilityRequest(user, period.id, deadline)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        if (!replyToLoaded.current) {
          setReplyTo(data.reply_to || "");
          replyToLoaded.current = true;
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "No se pudo armar la vista previa del pedido.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, period, user, deadline]);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const data = await sendAvailabilityRequest(user, period.id, {
        deadline: preview?.requires_deadline ? deadline : null,
        replyTo: replyTo.trim(),
        userIds: preview.recipients.map((r) => r.user_id),
      });
      setResult(data);
      onSent?.(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo enviar el pedido. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const missingDeadline = preview?.requires_deadline && !deadline;
  const done = result && result.failed.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Pedir disponibilidad
        <Typography variant="subtitle1" color="text.secondary">
          A los directores y evaluadores que todavía no la cargaron
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !preview && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity={preview ? "error" : "info"} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Alert severity={done ? "success" : "warning"} sx={{ mb: 2 }}>
            {result.detail}
          </Alert>
        )}

        {preview && !result && (
          <>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Sale un mail a cada uno ({preview.recipients.length}):
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
              {preview.recipients.map((r) => (
                <Chip
                  key={r.user_id}
                  size="small"
                  variant="outlined"
                  label={`${r.name} <${r.email}>`}
                />
              ))}
            </Box>
            {preview.bcc.length > 0 && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Con copia oculta a {preview.bcc.join(", ")} (de la plantilla).
              </Typography>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Responder a (Reply-To)"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                size="small"
                fullWidth
                helperText="Las respuestas a este mail llegan acá. Sin esto rebotan: el remitente es no-reply."
              />
              {preview.requires_deadline && (
                <TextField
                  label="Fecha límite para cargarla"
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: toInputDate(new Date()) }}
                  sx={{ minWidth: 220 }}
                  error={missingDeadline}
                  helperText={<>Va en el mail donde dice <code>$&#123;fecha_limite&#125;</code>.</>}
                />
              )}
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              Ejemplo: el mail que recibe {preview.example_for}.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Asunto:</strong> {preview.subject}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#fafafa" }}>
              <Typography
                variant="body2"
                component="div"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {preview.body_text}
              </Typography>
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button onClick={onEditTemplate} size="small">
          Editar plantilla
        </Button>
        <Box>
          <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>
            {result ? "Cerrar" : "Cancelar"}
          </Button>
          {!result && (
            <Button
              onClick={handleSend}
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              disabled={!preview || sending || loading || missingDeadline}
            >
              Enviar a {preview?.recipients.length ?? 0}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AvailabilityRequestDialog;
